import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dayjs, RootState, SafetyCrossDay, SafetyCrossStatistics, UUID } from '@umanforge/common';

import { VisualisationService } from 'services/visualisation.service';

import { EventManager } from 'interfaces/eventManager.interface';
import { EventCommitment } from 'interfaces/types.interface';
import { User, UserWithAvatar } from 'interfaces/user.interface';
import { GlobalService } from 'services/global.service';
import { EventManagerService } from 'services/eventManagers.service';
import { ChartWithDataApi } from 'interfaces/chartWithData.interface';


export const useVisualisationEventManagerBody = ( eventManager: EventManager, duration: number ) => {

  const dispatch = useDispatch();

  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );

  //-------------- useState ---------------\\
  const [ currentEventManager, setCurrentEventManager ] = useState<EventManager | null>( null );
  const [ engagementsMap, setEngagementsMap ] = useState<Map<string, Map<string, EventCommitment[]>>>( new Map() );
  const [ participants, setParticipants ] = useState<( User & { eventManagersId: UUID[]; } )[]>( [] );
  const [ chartsDatas, setChartsDatas ] = useState<ChartWithDataApi[]>( [] );
  const [ safetyCross, setSafetyCross ] = useState<{ incidents: SafetyCrossDay[], stats: SafetyCrossStatistics }>( { incidents: [], stats: { bestConsecutiveDays: 0, currentConsecutiveDays: 0, totalAccidents: 0 } } );
  const [ closeDaysByUsers, setCloseDaysByUsers ] = useState<{ [userId: UUID]: dayjs.Dayjs[] }>( {} );
  const [ displayDate, setDisplayDate ] = useState<dayjs.Dayjs>( dayjs() );
  const [ isAlone ] = useState<boolean>( duration === 0 );

  const elementRef = useRef<HTMLDivElement | null>( null );

  //-------------- useEffect --------------\\
  useEffect( () => {
    if ( eventManager ) {
      refreshAllDatasbyEventManger( eventManager, displayDate );
    }
  }, [ eventManager, displayDate ] );

  useEffect( () => {
    elementRef.current?.scrollTo( { top: 0, behavior: 'instant' } );
    setTimeout( () => {
      if (
        elementRef.current &&
        currentEventManager && currentEventManager.id === eventManager.id &&
        elementRef.current.scrollHeight > elementRef.current.clientHeight
      ) {
        scrollElement( elementRef.current, duration );
      }
    }, 100 );
  }, [ currentEventManager ] );

  //-------------- Functions --------------\\
  const refreshAllDatasbyEventManger = async ( newEventManager: EventManager, date: dayjs.Dayjs ) => {
    const updatedEngagements = await getEngagements( newEventManager.id, date );
    const updatedCloseDays = await getCloseDays( newEventManager.id, date );
    const updatedChartsDatas = await getChartsDatas( newEventManager.id );
    const updatedSafetyCross = await getSafetyCross( newEventManager.id );
    const updatedParticipants = getParticipants( newEventManager );

    setEngagementsMap( updatedEngagements );
    setCloseDaysByUsers( updatedCloseDays );
    setChartsDatas( updatedChartsDatas );
    setSafetyCross( updatedSafetyCross );
    setParticipants( updatedParticipants );

    if ( currentEventManager?.id != newEventManager.id ) {
      setCurrentEventManager( newEventManager );
    }
  };

  const getEngagements = async ( eventManagerId: UUID, date: dayjs.Dayjs ) => {
    const updatedUsers = await VisualisationService.getCommonUsersByEventManager( [ eventManagerId ], visualisationToken, dispatch );
    const updatedEngagements = await VisualisationService.getEngagements( eventManagerId, date, date, visualisationToken, dispatch );
    const updatedEngagementsMap = GlobalService.transformEventCommitmentsToMap( updatedEngagements, updatedUsers, [ date ] );
    return updatedEngagementsMap;
  };

  const getCloseDays = async ( eventManagerId: UUID, date: dayjs.Dayjs ) => {
    const updatedCloseDays = await VisualisationService.getMissingDayOfEventManagerByfUser( eventManagerId, date, date, visualisationToken, dispatch );
    const updatedCloseDaysByUser = GlobalService.transformRangedDateToListDate( updatedCloseDays );
    return updatedCloseDaysByUser;
  };

  const getChartsDatas = async ( eventManagerId: UUID ) => {
    const updatedChartsDatas = await VisualisationService.getChartsDatasOfEventManager( eventManagerId, visualisationToken, dispatch );
    return updatedChartsDatas;
  };

  const getSafetyCross = async ( eventManagerId: UUID ) => {
    const updatedSafetyCrossIncidents = await VisualisationService.getSafetyCrossOfEventManagerByMonth( eventManagerId, displayDate, visualisationToken, dispatch );
    const updatedSafetyCrossStats = await VisualisationService.getStatisticsOfSafetyCross( eventManagerId, visualisationToken, dispatch );
    return {
      incidents: updatedSafetyCrossIncidents,
      stats: updatedSafetyCrossStats
    };
  };

  const getParticipants = ( newEventManager: EventManager ) => {
    const updatedParticipants = EventManagerService.getOrderedParticipantsOfEventManager( newEventManager, me );
    return updatedParticipants;
  };

  const getParticipantById = ( id: UUID ): ( UserWithAvatar & { eventManagersId: UUID[]; } ) => {
    return (
      participants.find( participant => {
        return participant.id === id;
      } ) || { id: '', firstname: '', lastname: '', eventManagersId: [] }
    );
  };

  const isDateInMap = ( dateToCheck: dayjs.Dayjs, userId: UUID ): boolean => {
    const closeDaysOfUser = closeDaysByUsers[ userId ];
    let isClosed = false;
    if ( closeDaysOfUser ) {
      isClosed = closeDaysOfUser.some( closeDate => { return closeDate.isSame( dateToCheck, 'day' ); } );
    }
    return isClosed;
  };

  const scrollElement = ( element: HTMLDivElement | null, durationOfScroll: number ) => {
    const totalDuration = !isAlone ? durationOfScroll : ( eventManager.participants.length ) * 1000 * 1.5;
    const waitingTime = totalDuration * 0.1;
    const durationUsed = totalDuration - ( waitingTime * 2 );

    if ( element ) {
      const start = element.scrollTop;
      const end = element.scrollHeight - element.clientHeight;
      const distance = end - start;

      if ( distance > 0 ) {
        const animate = ( currentTime: number, startTime: number ) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min( elapsed / durationUsed, 1 );

          element.scrollTop = start + distance * progress;

          if ( progress < 1 ) {
            requestAnimationFrame( time => { animate( time, startTime ); } );
          } else {
            if ( isAlone ) {
              setTimeout( () => {
                element.scrollTo( { top: 0, behavior: 'instant' } );
                if ( !displayDate.isSame( dayjs(), 'day' ) ) {
                  setDisplayDate( dayjs() );
                } else {
                  scrollElement( element, durationOfScroll );
                }
              }, waitingTime );
            }
          }
        };

        setTimeout( () => {
          const startTime = performance.now();
          requestAnimationFrame( time => { animate( time, startTime ); } );
        }, waitingTime );
      }

    }
  };

  return { currentEventManager, engagementsMap, chartsDatas, safetyCross, displayDate, elementRef, isDateInMap, getParticipantById };
};